import React, { useState } from "react";
import { Phone, Mail, Clock, MapPin } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { services } from "../data/services";
import { groupACities, groupBCities } from "../data/serviceAreas";
import { QuoteModal } from "./QuoteModal";
import { SCOTTY_LOGO_URL } from "./ScottyLogo";
import {
  PHONE_DISPLAY,
  PHONE_LINK,
  EMAIL,
  EMAIL_LINK,
  HOURS,
  SLOGAN,
  BUSINESS_NAME,
  BUSINESS_ADDRESS,
} from "../data/business";

export const Footer: React.FC = () => {
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);

  return (
    <footer className="bg-[#252A2B] text-white pt-16 pb-8 border-t border-white/10">
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10 pb-14 border-b border-white/10">
          {/* Brand Info with Logo */}
          <div className="lg:col-span-4 space-y-3">
            <Link
              to="/"
              className="inline-block py-0.5 cursor-pointer text-left"
            >
              <img
                src={SCOTTY_LOGO_URL}
                alt="Scotty's Land Specialist Logo"
                className="h-28 sm:h-32 w-auto object-contain object-center drop-shadow-md transition-transform duration-200 hover:scale-105"
              />
            </Link>

            <p className="font-display text-2xl font-bold text-[#D96B27] uppercase tracking-wide">
              {SLOGAN}
            </p>

            <p className="text-white/80 text-xs sm:text-sm leading-relaxed max-w-sm">
              {BUSINESS_NAME} helps homeowners and businesses in Highland and
              surrounding Utah County communities keep their properties clean,
              clear, and usable through every season.
            </p>

            <p className="text-white/80 text-xs sm:text-sm leading-relaxed max-w-sm pt-1">
              Free estimates by call or text at 801-400-3346.
            </p>

            <div className="flex flex-col gap-2 pt-2 text-xs text-white/80">
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-[#D96B27]" />
                <span>{HOURS}</span>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 text-[#D96B27] shrink-0 mt-0.5" />
                <span>{BUSINESS_ADDRESS}</span>
              </div>
            </div>
          </div>

          {/* Services Links */}
          <div className="lg:col-span-3 space-y-3">
            <h3 className="font-display font-bold text-xs tracking-wider uppercase text-[#D96B27]">
              SERVICES
            </h3>
            <ul className="space-y-2 text-xs text-white/80">
              {services.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="hover:text-[#D96B27] transition-colors"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div className="lg:col-span-3 space-y-3">
            <h3 className="font-display font-bold text-xs tracking-wider uppercase text-[#D96B27]">
              SERVICE AREAS
            </h3>
            <ul className="grid grid-cols-2 gap-x-2 gap-y-1.5 text-xs text-white/80">
              <li className="col-span-2 pt-1 text-[10px] font-bold uppercase tracking-wider text-white/40">
                All Services
              </li>
              {groupACities.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/service-areas/${c.slug}`}
                    className="hover:text-[#D96B27] transition-colors"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
              <li className="col-span-2 pt-1 text-[10px] font-bold uppercase tracking-wider text-white/40">
                Weed Removal
              </li>
              {groupBCities.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/service-areas/${c.slug}`}
                    className="hover:text-[#D96B27] transition-colors"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Get In Touch */}
          <div className="lg:col-span-2 space-y-3">
            <h3 className="font-display font-bold text-xs tracking-wider uppercase text-[#D96B27]">
              GET IN TOUCH
            </h3>
            <ul className="space-y-3 text-xs text-white/80">
              <li>
                <a
                  href={PHONE_LINK}
                  className="flex items-center gap-2 hover:text-[#D96B27] transition-colors"
                >
                  <Phone className="w-3.5 h-3.5 text-[#D96B27]" />
                  {PHONE_DISPLAY}
                </a>
              </li>
              <li>
                <a
                  href={EMAIL_LINK}
                  className="flex items-start gap-2 hover:text-[#D96B27] transition-colors break-all"
                >
                  <Mail className="w-3.5 h-3.5 text-[#D96B27] shrink-0 mt-0.5" />
                  {EMAIL}
                </a>
              </li>
              <li className="pt-2 flex flex-col gap-3">
                <div className="flex flex-col items-center gap-1">
                  <a
                    href={PHONE_LINK}
                    className="inline-flex items-center justify-center gap-2 bg-[#D96B27] border-2 border-[#D96B27] hover:bg-[#BA541B] text-white px-4 py-2.5 rounded-full font-bold text-xs transition-colors w-full text-center shadow-md uppercase tracking-wide"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>{PHONE_DISPLAY}</span>
                  </a>
                  <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-white/80 text-center">
                    We text 24/7
                  </span>
                </div>
                <button
                  onClick={() => setIsQuoteOpen(true)}
                  className="inline-flex items-center justify-center bg-white border-2 border-white hover:bg-transparent text-[#252A2B] hover:text-white px-4 py-2.5 rounded-full font-bold text-xs transition-colors w-full text-center shadow-md uppercase tracking-wide"
                >
                  Get Free Quote
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright & Legal */}
        <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/50">
          <p>
            © {new Date().getFullYear()} {BUSINESS_NAME}. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link
              to="/blog"
              className="hover:text-white transition-colors"
            >
              Blog
            </Link>
            <Link
              to="/terms-and-conditions"
              className="hover:text-white transition-colors"
            >
              Terms and Conditions
            </Link>
            <Link
              to="/privacy-policy"
              className="hover:text-white transition-colors"
            >
              Privacy Policy
            </Link>
          </div>
          <p className="text-white/40">Highland, Utah · Serving Utah County</p>
        </div>
      </div>
      <QuoteModal isOpen={isQuoteOpen} onClose={() => setIsQuoteOpen(false)} />
    </footer>
  );
};
